import React from 'react';
import { Professor } from '../../services/professorService';
import './FilterPanel.css';

interface FilterPanelProps {
  universities: { id: number; name: string }[];
  departments: { id: number; name: string }[];
  researchAreas: { id: number; name: string }[];
  professors: Professor[];
  filters: {
    searchQuery: string;
    university: string;
    department: string;
    researchArea: string;
  };
  onFilterChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onClearFilters: () => void;
}

const FilterPanel: React.FC<FilterPanelProps> = ({
  universities,
  departments,
  researchAreas,
  professors,
  filters,
  onFilterChange,
  onClearFilters
}) => {
  // Get filtered professors based on current filters
  const getFilteredProfessors = () => {
    let filtered = [...professors];
    
    // Apply university filter if set
    if (filters.university) {
      filtered = filtered.filter(prof => prof.university === filters.university);
    }
    
    // Apply department filter if set
    if (filters.department) {
      filtered = filtered.filter(prof => prof.department === filters.department);
    }
    
    // Apply research area filter if set
    if (filters.researchArea) {
      filtered = filtered.filter(prof => 
        prof.research_areas?.some(area => area === filters.researchArea)
      );
    }
    
    return filtered;
  };
  
  // Get unique departments based on current filters
  const getUniqueDepartments = () => {
    let filteredProfs = [...professors];
    
    // Apply university filter if set
    if (filters.university) {
      filteredProfs = filteredProfs.filter(prof => prof.university === filters.university);
    }
    
    // Get unique departments
    const uniqueDepts = new Map();
    filteredProfs.forEach(prof => {
      if (prof.department) {
        // Create a unique key that combines university and department
        const key = filters.university ? prof.department : `${prof.university || 'Unknown'}: ${prof.department}`;
        uniqueDepts.set(key, {
          id: uniqueDepts.size + 1,
          name: key,
          university: prof.university,
          displayName: prof.department,
          count: (uniqueDepts.get(key)?.count || 0) + 1
        });
      }
    });
    
    return Array.from(uniqueDepts.values());
  };
  
  // Get unique universities based on current filters
  const getUniqueUniversities = () => {
    let filteredProfs = [...professors];
    
    // Apply department filter if set
    if (filters.department) {
      filteredProfs = filteredProfs.filter(prof => prof.department === filters.department);
    }
    
    // Apply research area filter if set
    if (filters.researchArea) {
      filteredProfs = filteredProfs.filter(prof => 
        prof.research_areas?.some(area => area === filters.researchArea)
      );
    }
    
    // Get unique universities
    const uniqueUniversities = new Map();
    filteredProfs.forEach(prof => {
      if (prof.university) {
        uniqueUniversities.set(prof.university, {
          id: uniqueUniversities.has(prof.university) ? uniqueUniversities.get(prof.university)!.id : uniqueUniversities.size + 1,
          name: prof.university,
          count: (uniqueUniversities.get(prof.university)?.count || 0) + 1
        });
      }
    });
    
    return Array.from(uniqueUniversities.values());
  };
  
  // Get unique research areas based on current filters
  const getUniqueResearchAreas = () => {
    let filteredProfs = [...professors];
    
    // Apply university filter if set
    if (filters.university) {
      filteredProfs = filteredProfs.filter(prof => prof.university === filters.university);
    }
    
    // Apply department filter if set
    if (filters.department) {
      filteredProfs = filteredProfs.filter(prof => prof.department === filters.department);
    }
    
    // Get unique research areas
    const uniqueAreas = new Map();
    filteredProfs.forEach(prof => {
      if (prof.research_areas && prof.research_areas.length > 0) {
        prof.research_areas.forEach(area => {
          uniqueAreas.set(area, {
            id: uniqueAreas.has(area) ? uniqueAreas.get(area)!.id : uniqueAreas.size + 1,
            name: area,
            count: (uniqueAreas.get(area)?.count || 0) + 1
          });
        });
      }
    });
    
    return Array.from(uniqueAreas.values());
  };
  
  // Get the currently filtered professors
  const filteredProfessors = getFilteredProfessors();
  return (
    <div className="filter-panel card mb-4">
      <div className="card-body">
        <h5 className="card-title mb-3">Search & Filter</h5>
        
        <div className="row g-3">
          <div className="col-md-6 filter-group">
            <div className="input-group">
              <span className="input-group-text">
                <i className="bi bi-search"></i>
              </span>
              <input
                type="text"
                className={`form-control ${filters.searchQuery ? 'has-value' : ''}`}
                placeholder="Search by name or research interest..."
                name="searchQuery"
                value={filters.searchQuery}
                onChange={onFilterChange}
              />
            </div>
          </div>
          
          <div className="col-md-6 d-flex justify-content-end">
            <button className="btn btn-outline-secondary" onClick={onClearFilters}>
              <i className="bi bi-x-circle me-1"></i>
              Clear Filters
            </button>
          </div>
        </div>
        
        <hr />
        
        <div className="row g-3">
          <div className="col-md-4 filter-group">
            <label htmlFor="universityFilter" className="form-label">University</label>
            {filters.university && <div className="active-filter-indicator"></div>}
            <select
              id="universityFilter"
              className={`form-select ${filters.university ? 'has-value' : ''}`}
              name="university"
              value={filters.university}
              onChange={onFilterChange}
            >
              <option value="">All Universities</option>
              {universities
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(uni => (
                  <option key={uni.id} value={uni.name}>
                    {uni.name}
                  </option>
                ))}
            </select>
          </div>
          
          <div className="col-md-4 filter-group">
            <label htmlFor="departmentFilter" className="form-label">Department</label>
            {filters.department && <div className="active-filter-indicator"></div>}
            <select
              id="departmentFilter"
              className={`form-select ${filters.department ? 'has-value' : ''}`}
              name="department"
              value={filters.department}
              onChange={onFilterChange}
            >
              <option value="">All Departments</option>
              {departments
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(dept => (
                  <option key={dept.id} value={dept.name}>
                    {dept.name}
                  </option>
                ))}
            </select>
          </div>
          
          <div className="col-md-4 filter-group">
            <label htmlFor="researchAreaFilter" className="form-label">Research Area</label>
            {filters.researchArea && <div className="active-filter-indicator"></div>}
            <select
              id="researchAreaFilter"
              className={`form-select ${filters.researchArea ? 'has-value' : ''}`}
              name="researchArea"
              value={filters.researchArea}
              onChange={onFilterChange}
            >
              <option value="">All Research Areas</option>
              {researchAreas
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(area => (
                  <option key={area.id} value={area.name}>
                    {area.name}
                  </option>
                ))}
            </select>
          </div>
        </div>
        
        <div className="filter-stats mt-3">
          <div className="d-flex justify-content-between align-items-center">
            <small className="text-muted">
              <i className="bi bi-info-circle me-1"></i>
              {(filters.university || filters.department || filters.researchArea) ? (
                <span>
                  <strong>Active filters:</strong> {[filters.university, filters.department, filters.researchArea].filter(Boolean).join(', ')}
                </span>
              ) : (
                <span>Tip: Use the search box to find professors by name, research area, or keywords in their research summary.</span>
              )}
            </small>
            <small className="text-muted">
              Showing: <span className="badge bg-primary">{filteredProfessors.length}</span> of <span className="badge bg-secondary">{professors.length}</span> professors
            </small>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;
